/**
 * Pull requests: the branch a Team's work starts from and its pull requests
 * open against, on the Repositories tab of Connections. GitHub's default
 * unless someone picks another; GPX's default deploys to production, so their
 * Teams open pull requests against staging.
 */
import { useEffect, useState } from "react";
import { Button, Input } from "../ui/index.js";
import type { useRepoPrBase } from "./useRepoPrBase.js";

type PrBaseState = ReturnType<typeof useRepoPrBase>;

export const PR_BASE_TITLE = "Open pull requests against";
export const PR_BASE_COPY = (defaultBranch: string) =>
  `Teams start their work from this branch and open their pull requests against it. Leave it empty for the GitHub default, ${defaultBranch}.`;
export const PR_BASE_READ_ONLY =
  "Workspace admins and the owner of a Team that works on it change this.";

/** The Pull requests row's summary: the branch they open against. */
export function prBaseSummary({ prBase, error }: PrBaseState): string {
  if (!prBase) return error ? "Could not be read just now" : "Reading";
  return prBase.branch
    ? `Opened against ${prBase.branch}`
    : `Opened against ${prBase.defaultBranch} (the GitHub default)`;
}

export function RepositoryPrBase({ repoId, state }: { repoId: string; state: PrBaseState }) {
  const { prBase, busy, error, save } = state;
  const [draft, setDraft] = useState("");
  useEffect(() => {
    setDraft(prBase?.branch ?? "");
  }, [prBase?.branch]);
  const saved = prBase?.branch ?? "";
  const changed = draft.trim() !== saved;
  const submit = async () => {
    const stored = await save(draft.trim() || null);
    if (stored !== false) setDraft(stored ?? "");
  };
  return (
    <div data-testid="repository-pr-base">
      {error && (
        <p role="alert" className="qa-notice">
          {error}
        </p>
      )}
      <div className="wrk-srow">
        <span className="wrk-srow-text">
          <label htmlFor={`pr-base-${repoId}`} className="wrk-srow-title">
            {PR_BASE_TITLE}
          </label>
          <span className="wrk-srow-help">{PR_BASE_COPY(prBase?.defaultBranch ?? "main")}</span>
          {prBase && !prBase.canEdit && <span className="wrk-srow-help">{PR_BASE_READ_ONLY}</span>}
        </span>
        <span className="wrk-srow-controls flex items-center gap-2">
          <Input
            id={`pr-base-${repoId}`}
            inputSize="sm"
            value={draft}
            placeholder={prBase?.defaultBranch ?? "main"}
            spellCheck={false}
            className="w-40 font-mono"
            disabled={!prBase || !prBase.canEdit || busy}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && changed) void submit();
            }}
          />
          {prBase?.canEdit && (
            <Button
              variant="secondary"
              size="sm"
              disabled={!changed || busy}
              loading={busy}
              onClick={() => void submit()}
            >
              Save
            </Button>
          )}
        </span>
      </div>
    </div>
  );
}
